import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ShieldCheck, Clock3, Handshake, Hammer, Sparkles } from "lucide-react";
import { FadeUp } from "../components/Reveal";
import { BookingForm } from "../components/BookingForm";
import { IMAGES } from "../lib/data";

const VALUES = [
  { icon: ShieldCheck, title: "Fully insured", text: "Public liability cover on every job, every visit." },
  { icon: Clock3, title: "On time, every time", text: "We turn up when we say we will — and finish when we promised." },
  { icon: Handshake, title: "Honest pricing", text: "Fixed, written quotes agreed before a single tool comes out." },
];

const TEAM = [
  {
    name: "Lewis",
    role: "Carpentry & Construction",
    text: "Framing, fencing, decking, roofing and structural repairs — the big builds and the tricky fixes.",
    accent: "text-lime-400",
    icon: Hammer,
  },
  {
    name: "Emily",
    role: "Maintenance & Finishing",
    text: "Property maintenance, repairs, installs and garden care — plus the detail work that makes a job feel finished.",
    accent: "text-purple-400",
    icon: Sparkles,
  },
];

export default function About() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash === "#booking") {
      const t = setTimeout(() => {
        document.getElementById("booking")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
      return () => clearTimeout(t);
    }
  }, [hash]);

  return (
    <main className="pt-40" data-testid="about-page">
      <div className="max-w-7xl mx-auto px-6">
        <FadeUp>
          <p className="font-mono-tech text-xs tracking-[0.3em] text-lime-400 uppercase mb-4">About us</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl text-zinc-50 leading-tight max-w-4xl mb-20">
            A husband &amp; wife team. <span className="italic text-lime-300">One standard.</span>
          </h1>
        </FadeUp>

        <div className="grid gap-16 lg:grid-cols-2 items-start mb-32">
          <FadeUp>
            <div className="rounded-2xl overflow-hidden border border-white/5">
              <img src={IMAGES.carpentry} alt="Careful carpentry work in progress" className="w-full h-[28rem] object-cover" />
            </div>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:pt-8">
            <p className="text-zinc-300 text-base md:text-lg font-light leading-relaxed mb-6">
              We're Lewis and Emily — a husband and wife team who started His &amp; Hers with a simple idea: most people don't need ten different tradespeople, they need one team they can trust with the whole list.
            </p>
            <p className="text-zinc-400 leading-relaxed mb-6">
              We look after homes, rentals and business premises across Moreton Bay, the Sunshine Coast and North Brisbane. Between us we cover carpentry, fencing, decking, landscaping, general repairs and all the odd jobs that never quite get done.
            </p>
            <p className="text-zinc-400 leading-relaxed">
              No call centres, no passing you from pillar to post. You speak to the people who'll actually do the work — and we'll treat your place like it's ours.
            </p>
          </FadeUp>
        </div>

        <FadeUp className="mb-12">
          <p className="font-mono-tech text-xs tracking-[0.3em] text-lime-400 uppercase mb-4">The His &amp; Hers difference</p>
          <h2 className="font-display text-4xl sm:text-5xl text-zinc-50">Meet the team</h2>
        </FadeUp>
        <div className="grid gap-6 md:grid-cols-2 mb-8" data-testid="team-section">
          {TEAM.map((t, i) => (
            <FadeUp key={t.name} delay={0.08 * i}>
              <div className="rounded-2xl border border-white/5 bg-[#0C0E0D] p-10 h-full transition-colors duration-500 hover:border-lime-400/30" data-testid={`team-card-${t.name.toLowerCase()}`}>
                <t.icon size={28} className={`${t.accent} mb-6`} />
                <h3 className="font-display text-3xl text-zinc-100 mb-1">{t.name}</h3>
                <p className={`font-mono-tech text-[11px] tracking-[0.2em] uppercase ${t.accent} mb-4`}>{t.role}</p>
                <p className="text-sm text-zinc-400 leading-relaxed">{t.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
        <FadeUp className="mb-32">
          <div className="rounded-2xl border border-purple-400/25 bg-purple-400/5 p-8 md:p-10" data-testid="team-preference-note">
            <p className="text-zinc-300 leading-relaxed max-w-3xl text-base md:text-lg font-light">
              Prefer a <span className="text-purple-300">female tradesperson</span> in your home — or happier with Lewis on the tools? Just tell us when you book and we'll send whoever you're most comfortable with. No awkwardness, no extra charge.
            </p>
          </div>
        </FadeUp>

        <div className="grid gap-6 md:grid-cols-3 mb-32">
          {VALUES.map((v, i) => (
            <FadeUp key={v.title} delay={0.08 * i}>
              <div className="rounded-2xl border border-white/5 bg-[#0C0E0D] p-8 h-full transition-colors duration-500 hover:border-lime-400/30" data-testid={`about-value-${i}`}>
                <v.icon size={26} className="text-purple-400 mb-5" />
                <h3 className="font-display text-xl text-zinc-100 mb-2">{v.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{v.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>

      <section id="booking" className="py-32 bg-[#0C0E0D] border-t border-white/5" data-testid="booking-section">
        <div className="max-w-7xl mx-auto px-6 grid gap-16 lg:grid-cols-2">
          <FadeUp>
            <p className="font-mono-tech text-xs tracking-[0.3em] text-lime-400 uppercase mb-4">Booking</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-zinc-50 leading-tight mb-6">
              Let's get it <span className="italic text-lime-300">sorted.</span>
            </h2>
            <p className="text-zinc-400 leading-relaxed max-w-md mb-8">
              Tell us what needs doing and we'll come back within one working day with next steps and a free, no-obligation quote. Every enquiry lands straight in our inbox.
            </p>
            <ul className="text-sm text-zinc-500 space-y-3">
              <li>— Free quotes, no obligation</li>
              <li>— Domestic &amp; commercial</li>
              <li>— No job too small</li>
              <li>— Request Lewis or Emily, whatever suits you</li>
            </ul>
          </FadeUp>
          <FadeUp delay={0.1}>
            <BookingForm />
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
